/**
 * BulkUpdateAttributesForm Component
 *
 * Form for selecting fields and values for bulk update operations.
 * Renders appropriate inputs (number/text) based on field type.
 */

import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';

interface FieldConfig {
  key: string;
  label: string;
  type: 'integer' | 'decimal' | 'text' | 'boolean';
  placeholder: string;
}

const AVAILABLE_FIELDS: FieldConfig[] = [
  { key: 'bedrooms', label: 'Bedrooms', type: 'integer', placeholder: '3' },
  { key: 'bathrooms', label: 'Bathrooms', type: 'decimal', placeholder: '2.5' },
  { key: 'accommodates', label: 'Accommodates', type: 'integer', placeholder: '6' },
  { key: 'basePrice', label: 'Base Price', type: 'decimal', placeholder: '150.00' },
  { key: 'originalCurrency', label: 'Currency', type: 'text', placeholder: 'EUR' },
  { key: 'latitude', label: 'Latitude', type: 'decimal', placeholder: '42.6977' },
  { key: 'longitude', label: 'Longitude', type: 'decimal', placeholder: '23.3219' },
  { key: 'addressCity', label: 'City', type: 'text', placeholder: 'Sofia' },
  { key: 'addressCountry', label: 'Country', type: 'text', placeholder: 'Bulgaria' },
  { key: 'addressState', label: 'State', type: 'text', placeholder: 'Sofia City' },
  { key: 'propertyType', label: 'Property Type', type: 'text', placeholder: 'Apartment' },
  { key: 'priceOnRequest', label: 'Price on Request', type: 'boolean', placeholder: '' },
];

interface FieldEntry {
  key: string;
  value: string;
}

interface BulkUpdateAttributesFormProps {
  onChange: (attributes: Record<string, unknown>) => void;
}

export function BulkUpdateAttributesForm({ onChange }: BulkUpdateAttributesFormProps): JSX.Element {
  const [entries, setEntries] = useState<FieldEntry[]>([]);

  const usedKeys = new Set(entries.map((e) => e.key));
  const availableForAdd = AVAILABLE_FIELDS.filter((f) => !usedKeys.has(f.key));

  const updateParent = (newEntries: FieldEntry[]): void => {
    const attributes: Record<string, unknown> = {};
    for (const entry of newEntries) {
      if (!entry.key) continue;
      const fieldConfig = AVAILABLE_FIELDS.find((f) => f.key === entry.key);
      if (!fieldConfig) continue;

      if (fieldConfig.type === 'boolean') {
        // Boolean fields: "true"/"false" string → boolean
        attributes[entry.key] = entry.value === 'true';
      } else if (entry.value === '') {
        continue;
      } else if (fieldConfig.type === 'integer') {
        const parsed = parseInt(entry.value, 10);
        if (!isNaN(parsed)) {
          attributes[entry.key] = parsed;
        }
      } else if (fieldConfig.type === 'decimal') {
        const parsed = parseFloat(entry.value);
        if (!isNaN(parsed)) {
          attributes[entry.key] = parsed;
        }
      } else {
        attributes[entry.key] = entry.value;
      }
    }
    onChange(attributes);
  };

  const addField = (key: string): void => {
    const fieldConfig = AVAILABLE_FIELDS.find((f) => f.key === key);
    const defaultValue = fieldConfig?.type === 'boolean' ? 'false' : '';
    const newEntries = [...entries, { key, value: defaultValue }];
    setEntries(newEntries);
    updateParent(newEntries);
  };

  const removeField = (index: number): void => {
    const newEntries = entries.filter((_, i) => i !== index);
    setEntries(newEntries);
    updateParent(newEntries);
  };

  const updateValue = (index: number, value: string): void => {
    const newEntries = entries.map((e, i) => (i === index ? { ...e, value } : e));
    setEntries(newEntries);
    updateParent(newEntries);
  };

  return (
    <div className="space-y-3" data-testid="bulk-update-attributes-form">
      <Label>Fields to Update</Label>

      {entries.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Add fields to set their values on all selected locations.
        </p>
      )}

      {entries.map((entry, index) => {
        const fieldConfig = AVAILABLE_FIELDS.find((f) => f.key === entry.key);
        return (
          <div key={entry.key} className="flex items-center gap-2">
            <div className="w-36 shrink-0">
              <span className="text-sm font-medium">{fieldConfig?.label ?? entry.key}</span>
            </div>
            {fieldConfig?.type === 'boolean' ? (
              <div className="flex-1 flex items-center gap-2">
                <Switch
                  checked={entry.value === 'true'}
                  onCheckedChange={(checked) => { updateValue(index, checked ? 'true' : 'false'); }}
                  data-testid={`bulk-update-field-${entry.key}`}
                />
                <span className="text-sm text-muted-foreground">
                  {entry.value === 'true' ? 'Yes' : 'No'}
                </span>
              </div>
            ) : (
              <Input
                type={fieldConfig?.type === 'text' ? 'text' : 'number'}
                step={fieldConfig?.type === 'decimal' ? '0.01' : '1'}
                placeholder={fieldConfig?.placeholder}
                value={entry.value}
                onChange={(e) => { updateValue(index, e.target.value); }}
                className="flex-1"
                data-testid={`bulk-update-field-${entry.key}`}
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => { removeField(index); }}
              className="shrink-0"
              data-testid={`bulk-update-remove-${entry.key}`}
            >
              <Trash2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        );
      })}

      {availableForAdd.length > 0 && (
        <Select
          value=""
          onValueChange={(key) => { addField(key); }}
        >
          <SelectTrigger className="w-full" data-testid="bulk-update-add-field-select">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Plus className="h-4 w-4" />
              <span>Add field</span>
            </div>
          </SelectTrigger>
          <SelectContent>
            {availableForAdd.map((field) => (
              <SelectItem key={field.key} value={field.key}>
                {field.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
